import { parseIncomeYen } from '../spouseSection/spouseEligibility';
import type {
  RetirementAllowanceEntry,
  RetirementAllowanceSnapshot,
  RetirementAllowanceStep,
} from './retirementAllowanceTypes';
import { RETIREMENT_ALLOWANCE_STEP_ORDER } from './retirementAllowanceTypes';

export function entryHasAmount(entry: RetirementAllowanceEntry): boolean {
  return parseIncomeYen(entry.retirementAllowanceAmount) > 0;
}

export function stepQuestionKey(step: RetirementAllowanceStep, taxYear: number): string {
  if (step === 'gate') {
    return 'submitDocuments.dependentDeduction.retirementAllowance.steps.gate';
  }
  return `submitDocuments.dependentDeduction.retirementAllowance.steps.${step}`;
}

export function gateQuestion(t: (k: string) => string, taxYear: number): string {
  return t('submitDocuments.dependentDeduction.retirementAllowance.steps.gate').replace(
    '{{taxYear}}',
    String(taxYear)
  );
}

export function stepIndex(step: RetirementAllowanceStep): number {
  return RETIREMENT_ALLOWANCE_STEP_ORDER.indexOf(step);
}

export function nextStepAfter(
  current: RetirementAllowanceStep,
  snap: RetirementAllowanceSnapshot
): RetirementAllowanceStep | null {
  switch (current) {
    case 'intro':
      return 'gate';
    case 'gate':
      return snap.anyExpected === 'yes' ? 'recipientType' : 'review';
    case 'recipientType':
      return snap.recipientCategory === 'spouse' ? 'pickSpouse' : 'pickDependent';
    case 'pickSpouse':
    case 'pickDependent':
      return 'enterAmount';
    case 'enterAmount':
      return snap.recipientCategory === 'spouse' ? 'review' : 'addAnotherDependent';
    case 'addAnotherDependent':
      return 'pickDependent';
    case 'review':
      return null;
    default:
      return null;
  }
}

export function undoTargetStep(
  current: RetirementAllowanceStep,
  snap: RetirementAllowanceSnapshot,
  committed: Set<RetirementAllowanceStep>,
  reviewMode: boolean
): RetirementAllowanceStep | null {
  if (reviewMode) {
    if (committed.has('addAnotherDependent') && snap.entries.length > 0) return 'addAnotherDependent';
    if (committed.has('enterAmount') && snap.entries.length > 0) {
      const last = snap.entries[snap.entries.length - 1];
      if (entryHasAmount(last)) return 'enterAmount';
    }
    if (committed.has('pickSpouse') || committed.has('pickDependent')) {
      return snap.recipientCategory === 'spouse' ? 'pickSpouse' : 'pickDependent';
    }
    if (committed.has('recipientType') && snap.recipientCategory !== '') return 'recipientType';
    if (committed.has('gate') && snap.anyExpected !== '') return 'gate';
    return null;
  }
  if (current.endsWith('Intro') || current === 'review') return null;
  const curIdx = stepIndex(current);
  let best: RetirementAllowanceStep | null = null;
  let bestIdx = -1;
  for (const s of RETIREMENT_ALLOWANCE_STEP_ORDER) {
    if (s === 'review') continue;
    if (!committed.has(s)) continue;
    const idx = stepIndex(s);
    if (idx < curIdx && idx > bestIdx) {
      bestIdx = idx;
      best = s;
    }
  }
  return best;
}

export function showUndoForStep(
  step: RetirementAllowanceStep,
  current: RetirementAllowanceStep,
  snap: RetirementAllowanceSnapshot,
  committed: boolean,
  reviewMode: boolean,
  allCommitted: Set<RetirementAllowanceStep>
): boolean {
  if (!committed || step === 'intro') return false;
  return undoTargetStep(current, snap, allCommitted, reviewMode) === step;
}

export function landOnStepAfterUndo(
  undoFrom: RetirementAllowanceStep,
  snap: RetirementAllowanceSnapshot
): RetirementAllowanceStep {
  if (undoFrom === 'enterAmount') {
    return snap.recipientCategory === 'spouse' ? 'pickSpouse' : 'pickDependent';
  }
  if (undoFrom === 'addAnotherDependent') {
    return snap.entries.length > 0 ? 'addAnotherDependent' : 'pickDependent';
  }
  return undoFrom;
}

export function retirementAllowanceCursor(step: RetirementAllowanceStep): string {
  return `retirementAllowance:${step}`;
}

export function parseRetirementAllowanceCursor(id: string | null | undefined): RetirementAllowanceStep | null {
  const m = (id ?? '').match(/^retirementAllowance:(.+)$/);
  if (!m) return null;
  const step = m[1] as RetirementAllowanceStep;
  return RETIREMENT_ALLOWANCE_STEP_ORDER.includes(step) ? step : null;
}

export function isRetirementAllowanceCursor(id: string | null | undefined): boolean {
  return (id ?? '').startsWith('retirementAllowance:');
}

export function buildResumeStep(
  snap: RetirementAllowanceSnapshot,
  sectionDone: boolean
): RetirementAllowanceStep {
  if (sectionDone && snap.anyExpected !== '') return 'review';
  if (snap.anyExpected === '') return 'gate';
  if (snap.anyExpected === 'no') return 'review';
  if (snap.recipientCategory === '') return 'recipientType';
  if (snap.recipientCategory === 'spouse') {
    if (snap.entries.length === 0) return 'pickSpouse';
    if (!entryHasAmount(snap.entries[0])) return 'enterAmount';
    return 'review';
  }
  if (snap.entries.length === 0) return 'pickDependent';
  const last = snap.entries[snap.entries.length - 1];
  if (!entryHasAmount(last)) return 'enterAmount';
  return 'addAnotherDependent';
}

/** True when the retirement gate is answered and any required recipients have amounts. */
export function isRetirementAllowanceComplete(snap: RetirementAllowanceSnapshot): boolean {
  if (snap.anyExpected === 'no') return true;
  if (snap.anyExpected !== 'yes') return false;
  if (snap.recipientCategory === '') return false;
  if (snap.entries.length < 1) return false;
  return snap.entries.every(entryHasAmount);
}
